/**
 * Task catalog — the master list of guided tasks shown on the `/tasks`
 * page. Each entry is a `TaskDefinition`; clicking "Run" in the catalog
 * UI mounts `<TaskRunnerModal>` with that definition.
 *
 * Monitor-only build: the device-onboarding wizards (Kasa, Shelly, Jebao,
 * probes, MQTT, rules) were removed along with all hardware control. The
 * remaining tasks are display/monitoring flows that point operators at
 * the surviving pages.
 *
 * To add a new task: implement the `TaskDefinition` (typically one file
 * per task in `apps/web/src/tasks/<name>/`), import it here, and append
 * to `TASK_CATALOG`. The Tasks page picks it up automatically.
 */
import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';

import type { TaskDefinition, TaskStepProps } from './types';
import { useEffect } from 'react';

// ─────────────────────────────────────────────────────────────────────────
// Placeholder step factory
// ─────────────────────────────────────────────────────────────────────────

interface PlaceholderProps {
  title: string;
  body: React.ReactNode;
}

/**
 * Generic single-step placeholder. Marks itself ready immediately so
 * the operator can click "Finish" to dismiss. The step body explains
 * where to go to complete the action today.
 */
function makePlaceholderStep(p: PlaceholderProps): ComponentType<TaskStepProps> {
  function Placeholder(props: TaskStepProps): JSX.Element {
    useEffect(() => {
      props.setReady(true);
      // Mount-only.
    }, []);
    return (
      <div className="flex flex-col gap-3" data-testid="task-placeholder">
        <h3 className="text-sm font-semibold text-amber-200">{p.title}</h3>
        <div className="text-sm text-reef-300 leading-relaxed">{p.body}</div>
        <p className="text-[11px] text-reef-500">
          A guided wizard for this task will land in a follow-up milestone.
          For now, use the linked page to complete the action.
        </p>
      </div>
    );
  }
  return Placeholder;
}

function placeholderTask(args: {
  id: string;
  title: string;
  description: string;
  tags: string[];
  note: string;
  body: React.ReactNode;
}): TaskDefinition {
  return {
    id: args.id,
    title: args.title,
    description: args.description,
    tags: args.tags,
    requires: { note: args.note },
    steps: [
      {
        id: 'placeholder',
        title: args.title,
        Component: makePlaceholderStep({ title: args.title, body: args.body }),
        nextLabel: 'Finish',
      },
    ],
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Catalog
// ─────────────────────────────────────────────────────────────────────────

export const TASK_CATALOG: readonly TaskDefinition[] = [
  placeholderTask({
    id: 'configure-dashboard',
    title: 'Customize your dashboard',
    description:
      'Pick which signals to show, group them, and arrange tile sizes.',
    tags: ['dashboard', 'tiles'],
    note: 'Use the Dashboard page Customize button.',
    body: (
      <p>
        Open the{' '}
        <Link to="/" className="text-amber-300 underline">
          Dashboard
        </Link>{' '}
        and click <em>Customize</em> in the header. The tile picker
        supports multi-select + sparkline previews so you can compose
        layouts faster.
      </p>
    ),
  }),

  placeholderTask({
    id: 'review-alarms',
    title: 'Review alarms & history',
    description:
      'See active alarms and audit past parameter readings.',
    tags: ['alarms', 'logs', 'audit'],
    note: 'Logs → Alarms / Parameter Logs.',
    body: (
      <p>
        Open{' '}
        <Link to="/logs/alarms" className="text-amber-300 underline">
          Logs → Alarms
        </Link>{' '}
        for active + recent alarms, or{' '}
        <Link to="/logs/parameters" className="text-amber-300 underline">
          Logs → Parameter Logs
        </Link>{' '}
        for the recorded parameter history.
      </p>
    ),
  }),
];

export type TaskCatalog = typeof TASK_CATALOG;
